import { call, put, all, takeLatest, takeLeading, delay, select } from "redux-saga/effects";
import * as api from "constants/api";
import urlconfig, { reportProductionApi, reportStagingApi } from "../_config";
import { createLoadingStatus, createSuccessStatus, createErrorStatus, extractErrorMessage, STATUS_MESSAGES } from "utilities/statusHelpers";
import { isStaplesOrg } from "utilities/organizationUtils";

import {
  DEBRIEF_SCHEDULES_PENDING,
  CULTURE_AUDIT_REPORT_PENDING,
  GET_DEBRIEF_SCHEDULES,
  SG_GET_DEBRIEF_SCHEDULES,
  GET_CULTURE_AUDIT_REPORTS,
  SG_GET_CULTURE_AUDIT_REPORTS,
  GET_GROUP_REPORT_PERMISSIONS,
  SG_GET_GROUP_REPORT_PERMISSIONS,
  DELETE_DEBRIEF_SCHEDULE,
  SG_DELETE_DEBRIEF_SCHEDULE,
  FETCH_DEBRIEF_SCHEDULE,
  SG_FETCH_DEBRIEF_SCHEDULE,
  EDIT_DEBRIEF_SCHEDULE,
  SG_EDIT_DEBRIEF_SCHEDULE,
  ADD_NEW_DEBRIEF_SCHEDULE,
  RESET_ADD_ACTIVE_PAGE_DEBRIEF_SCHEDULE,
  SG_ADD_DEBRIEF_SCHEDULE,
  SG_ADD_DEBRIEF_SCHEDULE_SUCCESS,
  SG_ADD_DEBRIEF_SCHEDULE_FAIL,
  GET_AUDIT_ELIGIBLE,
  SG_GET_AUDIT_ELIGIBLE,
  ADD_USER_TO_SURVEY,
  SG_ADD_USER_TO_SURVEY,
  EXTEND_SURVEY_TIME,
  SG_EXTEND_SURVEY_TIME,
  ALL_ERRORS,
  FIND_DEBRIEF_SCHEDULE,
  SG_FIND_DEBRIEF_SCHEDULE,
  GET_DEBRIEF_MEMBERS,
  SG_GET_DEBRIEF_MEMBERS,
  SG_ADD_DEBRIEF_SCHEDULE_ADD_USERS_BULK,
  ADD_DEBRIEF_SCHEDULE_ADD_USERS_BULK,
  SG_GET_CULTURE_AUDIT_REPORTS_V2,
  CULTURE_AUDIT_V2_TASK_UPDATE,
  SG_GET_CULTURE_AUDIT_CACHE_METADATA,
  GET_CULTURE_AUDIT_CACHE_METADATA,
  SG_CLEAR_CULTURE_AUDIT_CACHE,
  CLEAR_CULTURE_AUDIT_CACHE_RESULT,
  SG_ADMIN_CULTURE_AUDIT_PRE_POPULATE,
  ADMIN_CULTURE_AUDIT_PRE_POPULATE_RESULT,
  SG_ADMIN_CULTURE_AUDIT_PRE_POPULATE_BATCH,
  ADMIN_CULTURE_AUDIT_PRE_POPULATE_BATCH_RESULT,
  SG_ADMIN_CULTURE_AUDIT_CACHE_STATS,
  ADMIN_CULTURE_AUDIT_CACHE_STATS,
  UPDATE_CULTURE_AUDIT_STATUS
} from "constants/actions";


// Fetch debrief members for a given schedule
function* fetchDebriefMembers(action) {
  try {
    // action.payload: { id, query? }
    const json = yield call(api.GET_DEBRIEF_MEMBERS, action.payload);
    // Attach the schedule id so the reducer can bucket results
    yield put({ type: GET_DEBRIEF_MEMBERS, payload: { id: action.payload.id, data: json.data } });
  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
    // Still emit an empty bucket so UI doesn't hang waiting
    yield put({ type: GET_DEBRIEF_MEMBERS, payload: { id: action.payload.id, data: { results: [], count: 0 } } });
  }
}
export function* methodFetchDebriefMembers() {
  yield takeLatest(SG_GET_DEBRIEF_MEMBERS, fetchDebriefMembers);
}

function* loadmethods(action) {
    yield put({ type: DEBRIEF_SCHEDULES_PENDING });

  try {
    const json = yield call(api.GET_DEBRIEF_SCHEDULES, action.payload);
    // console.log(json.data)
    yield put({ type: GET_DEBRIEF_SCHEDULES, payload: json.data });

  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
  }
}

export function* methodLoad() {
  yield takeLatest(SG_GET_DEBRIEF_SCHEDULES, loadmethods);
}

// Helper to extract organization ID from payload
const extractOrganizationId = (payload) => {
  if (typeof payload === "string") {
    const params = new URLSearchParams(payload);
    const orgId = params.get("organization");
    console.log("Extracted organization ID from string:", orgId);
    return orgId;
  } else if (payload && typeof payload === "object") {
    const orgId = payload["organization"];
    console.log("Extracted organization ID from object:", orgId);
    return orgId;
  }
  console.log("No organization ID found in payload");
  return null;
};

// Throttle configuration
const THROTTLE_TIME = 15000; // 15 seconds between calls
let lastApiCallTime = 0;
let isV1CallPending = false; // Track if V1 API call is in progress
let isV2CallPending = false; // Track if V2 API call is in progress

function* loadreports(action) {
  // CRITICAL: Check Redux pending state AND module flag at the very start
  const reduxPending = yield select((state) => state.debrief_schedule?.reportsPending);
  if (reduxPending || isV1CallPending) {
    console.log("⏸️ V1 call already in progress (Redux pending:", reduxPending, "Module pending:", isV1CallPending, "), skipping duplicate request");
    return;
  }

  // Set pending flag immediately (synchronously, before any yields)
  isV1CallPending = true;

  try {
    // Safety check: Extract orgId from payload to verify it's not a Staples org
    const payloadOrgId = extractOrganizationId(action.payload);
    const storeOrgId = yield select((state) =>
      state.auth?.organization_id || state.auth?.selectedOrganization?.id
    );
    const orgId = payloadOrgId || storeOrgId;

    // Double-check: if this is a Staples org, route to V2 instead
    if (orgId && isStaplesOrg(orgId)) {
      console.log("⚠️ V1 flow blocked - Staples org detected, routing to V2 instead");
      // Clear V1 pending flag before routing to V2
      isV1CallPending = false;
      yield* loadCultureAuditV2(action);
      return;
    }

    // Step 1: Check if data is already loaded after previous call completed
    const existingData = yield select((state) => state.debrief_schedule?.culture_audit_reports?.response);
    const hasData = existingData && Array.isArray(existingData) && existingData.length > 0;

    // Step 2: Apply a global throttle between V1 calls, regardless of success or failure.
    // This protects the `/report/culture-audit` endpoint from being hammered when components
    // repeatedly dispatch on persistent errors (e.g. CORS / network failures).
    const currentTime = Date.now();
    const timeSinceLastCall = currentTime - lastApiCallTime;

    if (lastApiCallTime && timeSinceLastCall < THROTTLE_TIME) {
      const waitTime = THROTTLE_TIME - timeSinceLastCall;
      console.log(
        `⏸️ Throttling V1 culture audit call - waiting ${waitTime}ms before next request (last call at ${new Date(
          lastApiCallTime
        ).toISOString()})`
      );
      yield delay(waitTime);
    } else if (!hasData) {
      console.log("📊 No existing data found or first call, proceeding with V1 call immediately");
    }

    lastApiCallTime = Date.now();

    // Step 3: Make the API call
    yield put({ type: CULTURE_AUDIT_REPORT_PENDING });

    const json = yield call(api.GET_CULTURE_AUDIT_REPORTS, action.payload);
    yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: json.data });
  } catch (e) {
    // Surface the error
    yield put({ type: ALL_ERRORS, payload: e.response });
    // Provide a normalized empty payload so the UI can gracefully end loading and render "no data"
    yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: { response: [] } });
  } finally {
    // Clear pending flag when done (always, even on errors or early returns)
    isV1CallPending = false;
  }
}

// Router function that determines which flow to use based on org ID
function* routeCultureAuditReports(action) {
  console.log("🔀 routeCultureAuditReports - Action received:", action);

  // Get organization ID from Redux store (more reliable than payload)
  const storeOrgId = yield select((state) =>
    state.auth?.organization_id || state.auth?.selectedOrganization?.id
  );
  
  // Also try to extract from payload as fallback
  const payloadOrgId = extractOrganizationId(action.payload);
  
  // Use store orgId first, fallback to payload
  const organizationId = storeOrgId || payloadOrgId;
  
  console.log("📋 Organization ID from store:", storeOrgId, "from payload:", payloadOrgId, "using:", organizationId);

  // Validate organizationId before routing
  const orgId = Number(organizationId);
  if (!organizationId || isNaN(orgId) || orgId <= 0) {
    console.log("❌ Invalid organizationId, skipping culture audit call");
    yield put({ type: ALL_ERRORS, payload: { detail: "Invalid organization ID" } });
    yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: { response: [] } });
    return;
  }

  // Check if it's a Staples org
  const isStaples = isStaplesOrg(organizationId);
  console.log("🏢 Is Staples organization?", isStaples, "- Org ID:", organizationId);

  // If this is a Staples organization, route to V2 async flow
  if (isStaples) {
    console.log("✅ Routing to V2 async flow for Staples org:", organizationId);
    yield* loadCultureAuditV2(action);
  } else {
    // For non-Staples orgs, use the existing v1 flow
    console.log("✅ Routing to V1 flow for org:", organizationId);
    yield* loadreports(action);
  }
}

export function* reportLoad() {
  // Use takeLeading to ensure only ONE saga instance runs at a time
  // This prevents multiple concurrent calls even if actions are dispatched simultaneously
  yield takeLeading(SG_GET_CULTURE_AUDIT_REPORTS, routeCultureAuditReports);
}

// New: load group report permissions (v2 reports)
function* loadGroupReportPermissions(action) {
  const currentTime = Date.now();
  const throttle = localStorage.getItem("throttle");
  if (currentTime) {
    // mirror pending flag for reports
    yield put({ type: CULTURE_AUDIT_REPORT_PENDING });
  }
  try {
    // Support optional v1 fallback. Two ways to enable v1:
    // 1) Provide an object payload including { useV1: true } or { version: 'v1' }
    // 2) Set localStorage key 'REPORT_PERMS_VERSION' to 'v1'
    const localVersion = (localStorage.getItem("REPORT_PERMS_VERSION") || "v2").toLowerCase();

    let payloadQuery = "";
    let useV1 = false;

    if (typeof action.payload === "string") {
      // payload is already a query string. Also detect inline hints like version=v1 or useV1=true
      payloadQuery = action.payload;
      const lowered = action.payload.toLowerCase();
      if (lowered.includes("version=v1") || lowered.includes("usev1=true")) {
        useV1 = true;
      }
    } else if (action.payload && typeof action.payload === "object") {
      // Convert object to query string while extracting control flags
      const { useV1: pUseV1, version, ...rest } = action.payload;
      useV1 = Boolean(pUseV1) || String(version || "v2").toLowerCase() === "v1";
      const params = new URLSearchParams();
      Object.keys(rest || {}).forEach((key) => {
        const value = rest[key];
        if (value !== undefined && value !== null && value !== "") {
          params.append(key, String(value));
        }
      });
      payloadQuery = params.toString();
    }

    if (localVersion === "v1") {
      useV1 = true;
    }

    // Choose endpoint based on version flag
    // v2 (default): api.GET_GROUP_REPORT_PERMISSIONS -> /report/v2/culture-audit
    // v1 (legacy):  api.GET_CULTURE_AUDIT_REPORTS   -> /report/culture-audit
    const request = yield call(
      useV1 ? api.GET_CULTURE_AUDIT_REPORTS : api.GET_GROUP_REPORT_PERMISSIONS,
      payloadQuery
    );
    const json = request;
    yield put({ type: GET_GROUP_REPORT_PERMISSIONS, payload: json.data });
  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
    // Normalize to empty to unblock UI paths depending on this flag
    yield put({ type: GET_GROUP_REPORT_PERMISSIONS, payload: { response: [] } });
  }
}

export function* groupReportPermissionsLoad() {
  yield takeLatest(SG_GET_GROUP_REPORT_PERMISSIONS, loadGroupReportPermissions);
}

function* auditEligible(action) {
  yield put({ type: DEBRIEF_SCHEDULES_PENDING });
  try {
    const json = yield call(api.GET_AUDIT_ELIGIBLE, action.payload);
    // console.log(json.data)
    yield put({ type: GET_AUDIT_ELIGIBLE, payload: json.data });

  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
  }
}

export function* AuditEligibleLoad() {
  yield takeLatest(SG_GET_AUDIT_ELIGIBLE, auditEligible);
}

function* userAddToSurvey(action) {
  yield put({ type: DEBRIEF_SCHEDULES_PENDING });
  try {
    const json = yield call(api.ADD_DEBRIEF_SCHEDULE_ADD_USER, action.payload);
    // console.log(json.data)
    yield put({ type: ADD_USER_TO_SURVEY, payload: json.data });

  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
  }
}

export function* userAddToSurveyLoad() {
  yield takeLatest(SG_ADD_USER_TO_SURVEY, userAddToSurvey);
}

function* userExtendSurvey(action) {
  yield put({ type: DEBRIEF_SCHEDULES_PENDING });
  try {
    const json = yield call(api.ADD_DEBRIEF_SCHEDULE_EXTENDED_TIME, action.payload);
    // console.log(json.data)
    yield put({ type: EXTEND_SURVEY_TIME, payload: json.data });

  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
  }
}

export function* userExtendSurveyLoad() {
  yield takeLatest(SG_EXTEND_SURVEY_TIME, userExtendSurvey);
}


function* fetchmethods(action) {
  yield put({ type: DEBRIEF_SCHEDULES_PENDING });
  try {
    const json = yield call(api.FETCH_DEBRIEF_SCHEDULE, action.payload);
    yield put({ type: FETCH_DEBRIEF_SCHEDULE, payload: json.data });

  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
  }
}

export function* methodFetch() {
  yield takeLatest(SG_FETCH_DEBRIEF_SCHEDULE, fetchmethods);
}

function* updatemethods(action) {
  yield put({ type: DEBRIEF_SCHEDULES_PENDING });
  try {
    const json = yield call(api.EDIT_DEBRIEF_SCHEDULE, action.payload);
    yield put({ type: EDIT_DEBRIEF_SCHEDULE, payload: json.data });

  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
  }
}


export function* methodUpdate() {
  yield takeLatest(SG_EDIT_DEBRIEF_SCHEDULE, updatemethods);
}

function* addmethods(action) {
  yield put({ type: DEBRIEF_SCHEDULES_PENDING });
  yield put({ type: RESET_ADD_ACTIVE_PAGE_DEBRIEF_SCHEDULE });
  try {
    const json = yield call(api.ADD_DEBRIEF_SCHEDULE, action.payload);
    yield put({ type: ADD_NEW_DEBRIEF_SCHEDULE, payload: json.data });
    yield put({ type: SG_ADD_DEBRIEF_SCHEDULE_SUCCESS, payload: json.data });

  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
    yield put({ type: SG_ADD_DEBRIEF_SCHEDULE_FAIL, payload: e.response });
  }
}

export function* methodAdd() {
  yield takeLatest(SG_ADD_DEBRIEF_SCHEDULE, addmethods);
}

function* deletemethods(action) {
  yield put({ type: DEBRIEF_SCHEDULES_PENDING });
  try {
    yield call(api.DELETE_DEBRIEF_SCHEDULE, action.payload);
    yield put({ type: DELETE_DEBRIEF_SCHEDULE, payload: action.payload });

  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
  }
}

export function* methodDelete() {
  yield takeLatest(SG_DELETE_DEBRIEF_SCHEDULE, deletemethods);
}

function* findmethods(action) {
  yield put({ type: DEBRIEF_SCHEDULES_PENDING });
  try {
    const json = yield call(api.GET_DEBRIEF_SCHEDULES, action.payload);
    // console.log(json.data)
    yield put({ type: FIND_DEBRIEF_SCHEDULE, payload: json.data });

  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
  }
}

export function* methodFind() {
  yield takeLatest(SG_FIND_DEBRIEF_SCHEDULE, findmethods);
}

function* addUsersBulk(action) {
  // Follow the existing pattern: flip global pending while the request runs
  yield put({ type: DEBRIEF_SCHEDULES_PENDING });
  try {
    const json = yield call(api.ADD_DEBRIEF_SCHEDULE_ADD_USERS_BULK, action.payload);
    yield put({ type: ADD_DEBRIEF_SCHEDULE_ADD_USERS_BULK, payload: json.data });
  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response });
  }
}

export function* addUsersBulkLoad() {
  yield takeLatest(SG_ADD_DEBRIEF_SCHEDULE_ADD_USERS_BULK, addUsersBulk);
}

// V2 Culture Audit Async Report Flow
function* loadCultureAuditV2(action) {
  // CRITICAL: Check Redux pending state AND module flag at the very start
  const reduxPending = yield select((state) => state.debrief_schedule?.reportsPending);
  if (reduxPending || isV2CallPending) {
    console.log("⏸️ V2 call already in progress (Redux pending:", reduxPending, "Module pending:", isV2CallPending, "), skipping duplicate request");
    return;
  }

  // Set pending flag immediately (synchronously, before any yields)
  isV2CallPending = true;

  // Declare isStaples at function scope so it's accessible in catch block
  let isStaples = false;
  let organizationId = null;

  try {
    console.log("⚙️ loadCultureAuditV2 - Action payload:", action.payload);

    // Step 1: Check if data is already loaded after previous call completed
    const existingData = yield select((state) => state.debrief_schedule?.culture_audit_reports?.response);
    const hasData = existingData && Array.isArray(existingData) && existingData.length > 0;

    // Step 2: If data exists, apply throttle before making new call
    if (hasData) {
      const currentTime = Date.now();
      const timeSinceLastCall = currentTime - lastApiCallTime;

      if (timeSinceLastCall < THROTTLE_TIME) {
        const waitTime = THROTTLE_TIME - timeSinceLastCall;
        console.log(`⏸️ Throttling V2 culture audit call - waiting ${waitTime}ms (data already loaded)`);
        yield delay(waitTime);
      }
    } else {
      console.log("📊 No existing data found, proceeding with V2 call immediately");
    }

    lastApiCallTime = Date.now();

    // Helper to normalize and validate payload
    const normalizePayload = (payload) => {
    if (typeof payload === "string") {
      // Validate query string contains required params
      const params = new URLSearchParams(payload);
      const auditTypeId = params.get("audit-type-id");
      const organization = params.get("organization");
      const hasGroupId = !!params.get("group-id");

      if (!auditTypeId || !organization) {
        return { valid: false, queryString: null, organizationId: null, hasGroupId: false };
      }

      return { valid: true, queryString: payload, organizationId: organization, hasGroupId };
    } else if (payload && typeof payload === "object") {
      // Convert object to query string, supporting both camelCase and kebab-case keys
      // Exclude control flags like requireGroupId from the query string
      const { requireGroupId, ...rest } = payload;
      const auditTypeId = rest["auditTypeId"] || rest["audit-type-id"];
      const organization = rest["organization"];
      const hasGroupId = !!(rest["groupId"] || rest["group-id"]);

      if (!auditTypeId || !organization) {
        return { valid: false, queryString: null, organizationId: null, hasGroupId: false };
      }

      const params = new URLSearchParams();
      Object.keys(rest).forEach((key) => {
        const value = rest[key];
        if (value !== undefined && value !== null && value !== "") {
          // Normalize camelCase keys to kebab-case for API compatibility
          let normalizedKey = key;
          if (key === "auditTypeId") {
            normalizedKey = "audit-type-id";
          } else if (key === "groupId") {
            normalizedKey = "group-id";
          }
          params.append(normalizedKey, String(value));
        }
      });

      return { valid: true, queryString: params.toString(), organizationId: organization, hasGroupId };
    } else {
      // Invalid payload type
      return { valid: false, queryString: null, organizationId: null, hasGroupId: false };
    }
    };

    // Normalize and validate payload before proceeding
    const { valid, queryString, organizationId: payloadOrgId, hasGroupId } = normalizePayload(action.payload);
    organizationId = payloadOrgId; // Store at function scope for catch block

    // Check if group-id is required for group managers
    const requireGroupId = !!(action.payload && action.payload.requireGroupId);
    // Check if this is a Staples organization that needs special handling
    isStaples = organizationId ? isStaplesOrg(organizationId) : false;

    if (requireGroupId && !hasGroupId) {
      const groupError = { detail: "Group managers must include a group-id when requesting culture audit reports." };
      yield put({
        type: ALL_ERRORS,
        payload: groupError
      });
      yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: { response: [] } });
      yield put({
        type: CULTURE_AUDIT_V2_TASK_UPDATE,
        payload: { task_id: null, status: "failed", message: "Missing group-id for group manager" }
      });

      // Update toast status for Staples orgs - error
      if (isStaples) {
        yield put({
          type: UPDATE_CULTURE_AUDIT_STATUS,
          payload: createErrorStatus("culture_audit_reports", extractErrorMessage(groupError)),
        });
      }

      return;
    }

    if (!valid) {
      // Early return for invalid payload
      const invalidError = { detail: "Missing required params for culture audit report (audit-type-id, organization)." };
      yield put({ type: ALL_ERRORS, payload: invalidError });
      yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: { response: [] } });
      yield put({ type: CULTURE_AUDIT_V2_TASK_UPDATE, payload: { task_id: null, status: "failed", message: "Invalid payload" } });

      // Update toast status for Staples orgs - error
      if (isStaples) {
        yield put({
          type: UPDATE_CULTURE_AUDIT_STATUS,
          payload: createErrorStatus("culture_audit_reports", extractErrorMessage(invalidError)),
        });
      }

      return;
    }

    // Step 3: Start the API call
    yield put({ type: CULTURE_AUDIT_REPORT_PENDING });
    // Kick off async task
    const asyncResponse = yield call(api.GET_CULTURE_AUDIT_V2_ASYNC, queryString);
    const {
      task_id,
      status,
      message,
      check_status_url,
      cache_info,
    } = asyncResponse.data || {};

    const initialStatus = status || "queued";

    // Record initial task state (no heavy data here)
    // Always proceed to polling loop to fetch the full report from status endpoint
    yield put({
      type: CULTURE_AUDIT_V2_TASK_UPDATE,
      payload: {
        task_id: task_id || null,
        status: initialStatus,
        message: message || "",
        check_status_url: check_status_url || null,
        cache_info: cache_info || null,
      },
    });

    // Update toast status for Staples orgs
    if (isStaples) {
      const toastMessage = initialStatus === "queued"
        ? STATUS_MESSAGES.CULTURE_AUDIT_CACHE_LOADING || "Preparing culture audit report..."
        : STATUS_MESSAGES.CULTURE_AUDIT_PROCESSING || "Generating culture audit report...";
      yield put({
        type: UPDATE_CULTURE_AUDIT_STATUS,
        payload: createLoadingStatus("culture_audit_reports", toastMessage),
      });
    }

    // Polling loop
    const maxAttempts = 60;
    const intervalMs = 2000;
    let attempts = 0;
    let completed = false;

    while (attempts < maxAttempts && !completed) {
      yield delay(intervalMs);
      attempts++;

      try {
        const statusResponse = yield call(api.GET_CULTURE_AUDIT_V2_STATUS, task_id);
        const {
          status: currentStatus,
          message: currentMessage,
          report,
          metadata,
          progress,
        } = statusResponse.data || {};

        if (currentStatus === "completed") {
          if (report) {
            // 'report' contains { message, response: [...], organization, _cache_info, ... }
            yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: report });
            yield put({
              type: CULTURE_AUDIT_V2_TASK_UPDATE,
              payload: {
                task_id,
                status: currentStatus,
                message: currentMessage,
                progress: typeof progress === "number" ? progress : 100,
                metadata: metadata || null,
                cache_info: report._cache_info || null,
              },
            });

            // Update toast status for Staples orgs - success
            if (isStaples) {
              yield put({
                type: UPDATE_CULTURE_AUDIT_STATUS,
                payload: createSuccessStatus("culture_audit_reports", STATUS_MESSAGES.CULTURE_AUDIT_COMPLETED || "Culture audit report ready"),
              });
            }

            // Clear pending flag immediately so cache fetch can proceed without waiting
            isV2CallPending = false;
            completed = true;
            return;
          } else {
            // Status is completed but report not ready yet - this shouldn't happen, but if it does, stop polling
            yield put({
              type: CULTURE_AUDIT_V2_TASK_UPDATE,
              payload: {
                task_id,
                status: currentStatus,
                message: currentMessage || "Report completed but data not available",
                progress: 100,
                metadata: metadata || null,
              },
            });

            // Update toast status for Staples orgs - success (even though report not available)
            if (isStaples) {
              yield put({
                type: UPDATE_CULTURE_AUDIT_STATUS,
                payload: createSuccessStatus("culture_audit_reports", STATUS_MESSAGES.CULTURE_AUDIT_COMPLETED || "Culture audit report ready"),
              });
            }

            // Clear pending flag immediately so cache fetch can proceed without waiting
            isV2CallPending = false;
            completed = true;
            return;
          }
        }

        if (currentStatus === "failed") {
          const errorPayload = statusResponse.data || { detail: currentMessage || "Report generation failed" };
          yield put({ type: ALL_ERRORS, payload: errorPayload });
          yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: { response: [] } });
          yield put({ type: CULTURE_AUDIT_V2_TASK_UPDATE, payload: { task_id, status: currentStatus, message: currentMessage } });

          // Update toast status for Staples orgs - error
          if (isStaples) {
            const errorMessage = extractErrorMessage(errorPayload) || STATUS_MESSAGES.CULTURE_AUDIT_ERROR || "Failed to generate culture audit report";
            yield put({
              type: UPDATE_CULTURE_AUDIT_STATUS,
              payload: createErrorStatus("culture_audit_reports", errorMessage),
            });
          }

          // Clear pending flag immediately so cache fetch can proceed without waiting
          isV2CallPending = false;
          completed = true;
          return;
        }

        // Update status for queued/processing
        if (currentStatus === "queued" || currentStatus === "processing") {
          yield put({
            type: CULTURE_AUDIT_V2_TASK_UPDATE,
            payload: {
              task_id,
              status: currentStatus,
              message: currentMessage,
              progress: typeof progress === "number" ? progress : null,
              metadata: metadata || null,
            },
          });

          // Update toast status for Staples orgs - loading
          if (isStaples) {
            const toastMessage = currentStatus === "queued"
              ? STATUS_MESSAGES.CULTURE_AUDIT_CACHE_LOADING || "Preparing culture audit report..."
              : STATUS_MESSAGES.CULTURE_AUDIT_PROCESSING || "Generating culture audit report...";
            yield put({
              type: UPDATE_CULTURE_AUDIT_STATUS,
              payload: createLoadingStatus("culture_audit_reports", toastMessage),
            });
          }

          continue;
        }

        // Unknown status - treat as failure
        const unknownError = { detail: `Unknown status: ${currentStatus}` };
        yield put({ type: ALL_ERRORS, payload: unknownError });
        yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: { response: [] } });
        yield put({ type: CULTURE_AUDIT_V2_TASK_UPDATE, payload: { task_id, status: "failed", message: `Unknown status: ${currentStatus}` } });

        // Update toast status for Staples orgs - error
        if (isStaples) {
          yield put({
            type: UPDATE_CULTURE_AUDIT_STATUS,
            payload: createErrorStatus("culture_audit_reports", extractErrorMessage(unknownError)),
          });
        }

        // Clear pending flag immediately so cache fetch can proceed without waiting
        isV2CallPending = false;
        completed = true;
        return;
      } catch (pollError) {
        // If polling fails, continue but log error
        yield put({ type: ALL_ERRORS, payload: pollError.response || pollError });
        // Continue polling unless we've exhausted attempts
        if (attempts >= maxAttempts) {
          const pollingError = { detail: "Data failed to load, please refresh the page and try again." };
          yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: { response: [] } });
          yield put({ type: CULTURE_AUDIT_V2_TASK_UPDATE, payload: { task_id, status: "failed", message: "Data failed to load, please refresh the page and try again." } });

          // Update toast status for Staples orgs - error
          if (isStaples) {
            yield put({
              type: UPDATE_CULTURE_AUDIT_STATUS,
              payload: createErrorStatus("culture_audit_reports", extractErrorMessage(pollingError)),
            });
          }

          // Clear pending flag immediately so cache fetch can proceed without waiting
          isV2CallPending = false;
          completed = true;
          return;
        }
      }
    }

    // Timeout after maxAttempts (only if not already completed)
    if (!completed) {
      const timeoutError = { detail: "Data failed to load, please refresh the page and try again." };
      yield put({ type: ALL_ERRORS, payload: timeoutError });
      yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: { response: [] } });
      yield put({ type: CULTURE_AUDIT_V2_TASK_UPDATE, payload: { task_id, status: "failed", message: "Report generation timed out" } });

      // Update toast status for Staples orgs - error
      if (isStaples) {
        yield put({
          type: UPDATE_CULTURE_AUDIT_STATUS,
          payload: createErrorStatus("culture_audit_reports", extractErrorMessage(timeoutError)),
        });
      }

      // Clear pending flag immediately so cache fetch can proceed without waiting
      isV2CallPending = false;
    }
  } catch (e) {
    const catchError = e.response || e;
    yield put({ type: ALL_ERRORS, payload: catchError });
    yield put({ type: GET_CULTURE_AUDIT_REPORTS, payload: { response: [] } });
    yield put({ type: CULTURE_AUDIT_V2_TASK_UPDATE, payload: { task_id: null, status: "failed", message: "Report generation failed" } });

    // Update toast status for Staples orgs - error
    if (isStaples) {
      yield put({
        type: UPDATE_CULTURE_AUDIT_STATUS,
        payload: createErrorStatus("culture_audit_reports", extractErrorMessage(catchError)),
      });
    }

    // Clear pending flag immediately so cache fetch can proceed without waiting
    isV2CallPending = false;
  } finally {
    // Ensure pending flag is cleared (safety net for any edge cases)
    // Note: Most paths clear it earlier, but this ensures it's always cleared
    if (isV2CallPending) {
      isV2CallPending = false;
    }
  }
}

export function* reportLoadV2() {
  yield takeLatest(SG_GET_CULTURE_AUDIT_REPORTS_V2, routeCultureAuditReports);
}

// Cache Metadata Saga
function* loadCultureAuditCacheMetadata(action) {
  // Normalize payload to query string
  let queryString = "";
  if (typeof action.payload === "string") {
    queryString = action.payload;
  } else if (action.payload && typeof action.payload === "object") {
    const params = new URLSearchParams();
    Object.keys(action.payload).forEach((key) => {
      const value = action.payload[key];
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });
    queryString = params.toString();
  }

  try {
    const json = yield call(api.GET_CULTURE_AUDIT_CACHE_METADATA, queryString);
    yield put({ type: GET_CULTURE_AUDIT_CACHE_METADATA, payload: json.data });
  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response || e });
    yield put({ type: GET_CULTURE_AUDIT_CACHE_METADATA, payload: {} });
  }
}

export function* loadCultureAuditCacheMetadataLoad() {
  yield takeLatest(SG_GET_CULTURE_AUDIT_CACHE_METADATA, loadCultureAuditCacheMetadata);
}

// Clear Cache Saga
function* clearCultureAuditCache(action) {
  // Normalize payload to query string
  let queryString = "";
  if (typeof action.payload === "string") {
    queryString = action.payload;
  } else if (action.payload && typeof action.payload === "object") {
    const params = new URLSearchParams();
    Object.keys(action.payload).forEach((key) => {
      const value = action.payload[key];
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });
    queryString = params.toString();
  }

  try {
    const json = yield call(api.CLEAR_CULTURE_AUDIT_CACHE, queryString);
    yield put({ type: CLEAR_CULTURE_AUDIT_CACHE_RESULT, payload: json.data });
  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response || e });
  }
}

export function* clearCultureAuditCacheLoad() {
  yield takeLatest(SG_CLEAR_CULTURE_AUDIT_CACHE, clearCultureAuditCache);
}

// Admin Pre-populate (Single) Saga
function* adminPrePopulate(action) {
  // Normalize payload to query string
  let queryString = "";
  if (typeof action.payload === "string") {
    queryString = action.payload;
  } else if (action.payload && typeof action.payload === "object") {
    const params = new URLSearchParams();
    Object.keys(action.payload).forEach((key) => {
      const value = action.payload[key];
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });
    queryString = params.toString();
  }

  try {
    const json = yield call(api.ADMIN_CULTURE_AUDIT_PRE_POPULATE, queryString);
    yield put({ type: ADMIN_CULTURE_AUDIT_PRE_POPULATE_RESULT, payload: json.data });
  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response || e });
  }
}

export function* adminPrePopulateLoad() {
  yield takeLatest(SG_ADMIN_CULTURE_AUDIT_PRE_POPULATE, adminPrePopulate);
}

// Admin Pre-populate (Batch) Saga
function* adminPrePopulateBatch(action) {
  try {
    const json = yield call(api.ADMIN_CULTURE_AUDIT_PRE_POPULATE_BATCH, action.payload);
    yield put({ type: ADMIN_CULTURE_AUDIT_PRE_POPULATE_BATCH_RESULT, payload: json.data });
  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response || e });
  }
}

export function* adminPrePopulateBatchLoad() {
  yield takeLatest(SG_ADMIN_CULTURE_AUDIT_PRE_POPULATE_BATCH, adminPrePopulateBatch);
}

// Admin Cache Stats Saga
function* adminCacheStats(action) {
  // Normalize optional filter payload to query string
  let queryString = "";
  if (typeof action.payload === "string") {
    queryString = action.payload;
  } else if (action.payload && typeof action.payload === "object") {
    const params = new URLSearchParams();
    Object.keys(action.payload).forEach((key) => {
      const value = action.payload[key];
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });
    queryString = params.toString();
  }

  try {
    const json = yield call(api.ADMIN_CULTURE_AUDIT_CACHE_STATS, queryString);
    yield put({ type: ADMIN_CULTURE_AUDIT_CACHE_STATS, payload: json.data });
  } catch (e) {
    yield put({ type: ALL_ERRORS, payload: e.response || e });
  }
}

export function* adminCacheStatsLoad() {
  yield takeLatest(SG_ADMIN_CULTURE_AUDIT_CACHE_STATS, adminCacheStats);
}

export default function* index() {
  yield all([
    methodLoad(),
    reportLoad(),
    groupReportPermissionsLoad(),
    AuditEligibleLoad(),
    userAddToSurveyLoad(),
    userExtendSurveyLoad(),
    methodFetch(),
    methodUpdate(),
    methodAdd(),
    methodDelete(),
    methodFind(),
    methodFetchDebriefMembers(),
    addUsersBulkLoad(),
    reportLoadV2(),
    loadCultureAuditCacheMetadataLoad(),
    clearCultureAuditCacheLoad(),
    adminPrePopulateLoad(),
    adminPrePopulateBatchLoad(),
    adminCacheStatsLoad(),
  ]);
}
